import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {
  getGuidNullable,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import type {
  DragDropState,
  DragSource,
  DropAnchor,
} from '@packages/systems/designer/dnd';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {
  getInnermostSymbolInstance,
  getRelativePath,
  isPathToSymbolRoot,
  nativeIdPathsEqual,
} from '@packages/utilities/native-id-paths';
import {isSlot} from '@packages/utilities/elementTypeUtils';
import {
  getCurrentComponentRender,
  type RelativePositionPlacementOptions,
} from '@packages/utilities/core-elements';
import {Expressions, type ExpressionPath} from '@packages/systems/wfdl';
import type {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  getTargetedSlotAnchorPath,
  resolveSlotDefaultMoveSource,
  resolveSlotDefaultPathContext,
  resolveSlotDefaultPathContextInFocus,
  type SlotDefaultMoveSource,
} from '@packages/systems/component-slots';

// The questions the generic drag/drop layer is allowed to ask about Slot
// defaults, and the reads those answers imply. Deliberately free of any
// DragDropUtils import so the generic layer can depend on this without a cycle.
//
// Every read here is against committed components. Slot defaults live on
// `component.defaultData`, and `stagedComponents` is a drag-preview fork that
// can describe a tree that no longer exists — the mover already resolves
// against committed state, so this side has to agree with it.
//
// The projected path reads the Slot-defaults flag only here, and only the
// anchor side consults it. Projected rows show up in the Navigator whether the
// flag is on or not, so a drag that already started has to be able to resolve
// its source or it breaks silently. Offering a drop is the new behavior, so
// that is what is gated. Inline definition-Slot authoring is a separate feature
// and reads the flag on its own, in DragDropUtils.
const canPlaceIntoSlotDefaults = () =>
  getFeatureFlagOutsideOfReact(FEATURE_FLAGS.COMPONENT_SLOT_DEFAULTS);

const LIVE_SOURCE: DragSource = {kind: 'live'};
const LIVE_ANCHOR: DropAnchor = {kind: 'live'};

export const isInFocusedRender = (
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  designerState: DesignerStateType
): boolean => {
  if (!focusedSymbolNativeIdPath.length) return false;
  const relativePath = getRelativePath(nativeIdPath, focusedSymbolNativeIdPath);
  const isNestedInstanceRoot =
    relativePath.length === 2 &&
    isPathToSymbolRoot(nativeIdPath, designerState.components);
  if (relativePath.length !== 1 && !isNestedInstanceRoot) return false;

  const elementId = getGuidNullable(relativePath[0]);
  if (!elementId) return false;
  const focusedRoot = getCurrentComponentRender(
    designerState.components,
    focusedSymbolNativeIdPath
  );
  if (!focusedRoot) return false;

  return Expressions.getElementById(focusedRoot, elementId) !== null;
};

/**
 * Decides whether a drag picked up Slot default content.
 *
 * Callers pass the path the row was addressed by. A `live` verdict means the
 * element is findable in the expanded render the way any ordinary drag source is.
 *
 * Deliberately ungated: see the note at the top of the file.
 */
export const resolveDragSourceKind = ({
  designerState,
  uiNodeState,
  nativeIdPath,
}: {
  designerState: DesignerStateType;
  uiNodeState: {focusedSymbolNativeIdPath: NativeId[]};
  nativeIdPath: NativeId[] | null | undefined;
}): DragSource => {
  if (!nativeIdPath?.length) return LIVE_SOURCE;

  const context = resolveSlotDefaultPathContextInFocus(
    nativeIdPath,
    uiNodeState.focusedSymbolNativeIdPath,
    designerState.components,
    designerState.plugins
  );
  if (!context) return LIVE_SOURCE;

  return {kind: 'slotDefault', nativeIdPath};
};

/**
 * Decides what tree a drop anchor is authored in.
 *
 * A Slot anchor inside a component instance is `liveSlot`, which is pre-existing
 * behavior. `slotDefault` wins when the anchor also resolves to projected
 * default content, because that is the ownership the write has to respect.
 */
export const resolveDropAnchor = ({
  designerState,
  uiNodeState,
  anchorElement,
  nativeIdPath,
  anchorPosition,
}: {
  designerState: DesignerStateType;
  uiNodeState: {focusedSymbolNativeIdPath: NativeId[]};
  anchorElement: ExpressionElement;
  nativeIdPath: NativeId[] | null | undefined;
  anchorPosition: RelativePositionPlacementOptions;
}): DropAnchor => {
  if (!nativeIdPath?.length) return LIVE_ANCHOR;

  const components = designerState.components;
  const isAnchorInFocusedRender = isInFocusedRender(
    nativeIdPath,
    uiNodeState.focusedSymbolNativeIdPath,
    designerState
  );
  if (isAnchorInFocusedRender && !isSlot(anchorElement)) return LIVE_ANCHOR;

  // Beside a Slot is a sibling of the Slot rather than content for it — the same
  // rule getSlotDefaultDropFieldId applies on the write side — but only where a
  // sibling is reachable. A Slot the edited definition declares sits in its
  // render; one inside projected content does not, so beside that one still
  // means beside the instance holding it, and the projection has to resolve it.
  if (
    isAnchorInFocusedRender &&
    isSlot(anchorElement) &&
    (anchorPosition === 'before' || anchorPosition === 'after')
  ) {
    return LIVE_ANCHOR;
  }

  // The anchor question is asked of the Slot's owning instance. A Slot inside a
  // projected instance never proxies up to one, so resolve it from the path.
  let slotOwner = anchorElement;
  if (isSlot(anchorElement)) {
    slotOwner =
      getInnermostSymbolInstance(
        nativeIdPath,
        components,
        designerState.plugins
      ) ?? anchorElement;
  }

  const slotAnchorPath = getTargetedSlotAnchorPath(
    nativeIdPath,
    uiNodeState.focusedSymbolNativeIdPath,
    slotOwner,
    components,
    designerState.plugins
  );

  if (!isAnchorInFocusedRender && canPlaceIntoSlotDefaults()) {
    const context = resolveSlotDefaultPathContextInFocus(
      nativeIdPath,
      uiNodeState.focusedSymbolNativeIdPath,
      components,
      designerState.plugins,
      slotAnchorPath
    );
    if (context) {
      return {kind: 'slotDefault', nativeIdPath, path: slotAnchorPath};
    }
  }

  if (slotAnchorPath) {
    return {kind: 'liveSlot', nativeIdPath, path: slotAnchorPath};
  }
  return LIVE_ANCHOR;
};

/**
 * Same question as `resolveDropAnchor`, for callers that only have a hovered
 * path and no resolved anchor element yet. Answers whether the path addresses
 * Slot default content, without the live-Slot distinction.
 */
export const isSlotDefaultPath = ({
  designerState,
  uiNodeState,
  nativeIdPath,
  anchorPath,
}: {
  designerState: DesignerStateType;
  uiNodeState: {focusedSymbolNativeIdPath: NativeId[]};
  nativeIdPath: NativeId[] | null | undefined;
  anchorPath?: ExpressionPath | null;
}): boolean => {
  if (!nativeIdPath?.length) return false;
  if (!canPlaceIntoSlotDefaults()) return false;
  if (
    isInFocusedRender(
      nativeIdPath,
      uiNodeState.focusedSymbolNativeIdPath,
      designerState
    )
  )
    return false;

  return Boolean(
    resolveSlotDefaultPathContextInFocus(
      nativeIdPath,
      uiNodeState.focusedSymbolNativeIdPath,
      designerState.components,
      designerState.plugins,
      anchorPath
    )
  );
};

/**
 * Whether a hovered Slot may accept a drop that authors its default. Separate
 * from `resolveDropAnchor` because the hover loop asks about the Slot element
 * itself, before an anchor has been proposed.
 */
export const canDropIntoSlotDefault = ({
  designerState,
  uiNodeState,
  hoveredElement,
  hoveredNativeIdPath,
}: {
  designerState: DesignerStateType;
  uiNodeState: {focusedSymbolNativeIdPath: NativeId[]};
  hoveredElement: ExpressionElement;
  hoveredNativeIdPath: NativeId[] | null | undefined;
}): boolean => {
  if (!hoveredNativeIdPath?.length) return false;
  if (!canPlaceIntoSlotDefaults()) return false;
  if (!isSlot(hoveredElement)) return false;

  const components = designerState.components;
  const ownerInstance = getInnermostSymbolInstance(
    hoveredNativeIdPath,
    components,
    designerState.plugins
  );
  if (!ownerInstance) return false;

  const ownerAnchorPath = getTargetedSlotAnchorPath(
    hoveredNativeIdPath,
    uiNodeState.focusedSymbolNativeIdPath,
    ownerInstance,
    components,
    designerState.plugins
  );

  return Boolean(
    resolveSlotDefaultPathContext(
      hoveredNativeIdPath,
      components,
      designerState.plugins,
      ownerAnchorPath
    )
  );
};

/**
 * The Slot-default source a drag is carrying, resolved for read. `blocked` means
 * the path pointed at default content we cannot move, so the drag should not start.
 */
type SourceView =
  | {kind: 'live'}
  | {kind: 'blocked'}
  | {
      kind: 'slotDefault';
      element: ExpressionElement;
      parentElement: ExpressionElement | null;
      nativeIdPath: NativeId[];
    };

const resolveSourceView = ({
  designerState,
  dragSource,
}: {
  designerState: DesignerStateType;
  dragSource: DragSource;
}): SourceView => {
  if (dragSource.kind === 'live') return {kind: 'live'};
  const sourceNativeIdPath = dragSource.nativeIdPath;

  const source = resolveSlotDefaultMoveSource({
    sourceNativeIdPath,
    componentMap: designerState.components,
    plugins: designerState.plugins,
  });
  if (!source) return {kind: 'blocked'};

  let sourcePathIndex = -1;
  for (let index = source.nativeIdPath.length - 1; index >= 0; index--) {
    if (getGuidNullable(source.nativeIdPath[index]) === source.element.id) {
      sourcePathIndex = index;
      break;
    }
  }
  let parent: SlotDefaultMoveSource | null = null;
  if (sourcePathIndex > 0) {
    parent = resolveSlotDefaultMoveSource({
      sourceNativeIdPath: source.nativeIdPath.slice(0, sourcePathIndex),
      componentMap: designerState.components,
      plugins: designerState.plugins,
    });
  }
  return {
    kind: 'slotDefault',
    element: source.element,
    parentElement: parent?.element ?? null,
    nativeIdPath: source.nativeIdPath,
  };
};

/**
 * The path a projected drag was picked up from, or null for a live drag.
 *
 * Doubles as the projected-or-not question: only Slot-default sources carry a
 * path, so a non-null result means the drag is projected.
 */
export const resolveProjectedSourcePath = (
  dragSource: DragSource | null | undefined
): NativeId[] | null => {
  if (dragSource?.kind !== 'slotDefault') return null;
  return dragSource.nativeIdPath;
};

/**
 * Whether the pointer is over the drag preview of the element it is carrying.
 *
 * A projected drag renders its preview under the pointer, so the pointer lands on
 * the very element being dragged. Keep the projected slot target that produced the
 * preview until the pointer leaves, rather than retargeting onto the preview.
 */
export const isHoveringOwnPreview = (
  dragDropState: DragDropState,
  hoveredElement: ExpressionElement,
  dragElement: ExpressionElement | null,
  hoveredNativeIdPath: NativeId[]
): boolean => {
  if (dragDropState.anchorOwnership !== 'slotDefault') return false;
  if (!dragElement || hoveredElement.id !== dragElement.id) return false;

  // Two Slots project the same default, so a sibling tree holds this very id.
  // Only the path says whether the pointer is over the preview or over the
  // other tree's copy of it, which is somewhere the drag may legitimately go.
  const sourceNativeIdPath = resolveProjectedSourcePath(
    dragDropState.dragSource
  );
  if (!sourceNativeIdPath) return true;

  return nativeIdPathsEqual(sourceNativeIdPath, hoveredNativeIdPath);
};

/**
 * The projected element a drag is carrying, or null when the source is live.
 * For callers that already have their own live lookup and only need the case
 * the render cannot answer.
 */
export const resolveProjectedSourceElement = ({
  designerState,
  dragSource,
}: {
  designerState: DesignerStateType;
  dragSource: DragSource;
}): ExpressionElement | null => {
  const view = resolveSourceView({designerState, dragSource});
  if (view.kind !== 'slotDefault') return null;
  return view.element;
};

/**
 * What a drag is carrying, for callers that need the element and usually its
 * parent. Slot-default content is absent from the render, so the projection
 * answers for it and a live drag falls through to the ordinary lookup.
 *
 * `parent` is a thunk because callers can bail before needing it, and the walk
 * is not worth paying for on a frame that returns early.
 */
export type DragSourceContent = {
  element: ExpressionElement;
  parent: () => ExpressionElement | null;
};

export const resolveDragSourceContent = ({
  designerState,
  dragSource,
  root,
  dragElementId,
}: {
  designerState: DesignerStateType;
  dragSource: DragSource;
  root: Expression;
  dragElementId: string | null;
}): DragSourceContent | null => {
  const view = resolveSourceView({designerState, dragSource});

  if (view.kind === 'slotDefault') {
    return {element: view.element, parent: () => view.parentElement};
  }

  if (!dragElementId) return null;
  const element = Expressions.getElementById(root, dragElementId);
  if (!element) return null;

  return {element, parent: () => Expressions.getParent(root, element.id)};
};

/**
 * Whether a drag may start, and the projected content it is carrying if any.
 *
 * `isBlocked` means the path pointed at default content we cannot move, so the
 * drag should not start. `projected` is null for an ordinary live drag.
 */
export const inspectDragSource = ({
  designerState,
  dragSource,
}: {
  designerState: DesignerStateType;
  dragSource: DragSource;
}): {
  isBlocked: boolean;
  projected: {element: ExpressionElement; nativeIdPath: NativeId[]} | null;
} => {
  const view = resolveSourceView({designerState, dragSource});

  if (view.kind === 'slotDefault') {
    return {
      isBlocked: false,
      projected: {element: view.element, nativeIdPath: view.nativeIdPath},
    };
  }

  return {isBlocked: view.kind === 'blocked', projected: null};
};
